import { useCallback, useEffect, useState } from "react";

const CHECK_STATUS_LABELS = {
  unconfirmed:"未確認",
  entered:"実費あり確認",
  zero_confirmed:"0円確認",
  not_applicable:"対象外",
};

const yen = value => `${Number(value || 0).toLocaleString("ja-JP")}円`;

const auditActionLabel = action => action === "reopen" ? "再オープン" : "月締め";
const formatAuditDateTime = value => String(value || "").replace(
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?/,
  "$1/$2/$3 $4:$5",
);

function BillingCheckRow({ check, canConfirm, onUpdate }) {
  const [note, setNote] = useState(check.note || "");
  const [saving, setSaving] = useState(false);
  useEffect(()=>setNote(check.note || ""), [check.id, check.note]);

  const update = async changes => {
    setSaving(true);
    try { await onUpdate(check.id, changes); } finally { setSaving(false); }
  };
  const hasJippi = Boolean(check.jippi?.exists);
  return <tr>
    <td style={{ padding:"9px 7px", verticalAlign:"top" }}>
      <div style={{ fontWeight:700 }}>{check.display_service_user_name}</div>
      <div style={{ fontSize:10, color:"#64748b" }}>{check.service_user_id}</div>
    </td>
    <td style={{ padding:"9px 7px", verticalAlign:"top" }}>
      <div style={{ fontWeight:700 }}>{check.item_label}</div>
      <code style={{ fontSize:10 }}>{check.item_code}</code>
    </td>
    <td style={{ padding:"9px 7px", verticalAlign:"top" }}>
      <span style={{ color:hasJippi ? "#166534" : "#b45309", fontWeight:800 }}>
        {hasJippi ? `実費あり（${check.jippi.count}件）` : "実費未入力／要確認"}
      </span>
      {hasJippi && <div style={{ fontSize:11, marginTop:2 }}>合計 {yen(check.jippi.total_amount)}</div>}
    </td>
    <td style={{ padding:"9px 7px", verticalAlign:"top" }}>
      <div style={{ fontWeight:700 }}>{CHECK_STATUS_LABELS[check.status] || check.status}</div>
      {canConfirm && <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:5 }}>
        {hasJippi && check.status !== "entered" && <button type="button" disabled={saving} onClick={()=>update({ status:"entered" })}>実費ありを確認</button>}
        {!hasJippi && check.status !== "zero_confirmed" && <button type="button" disabled={saving} onClick={()=>update({ status:"zero_confirmed" })}>0円確認</button>}
        {!hasJippi && check.status !== "not_applicable" && <button type="button" disabled={saving} onClick={()=>update({ status:"not_applicable" })}>対象外</button>}
        {check.status !== "unconfirmed" && <button type="button" disabled={saving} onClick={()=>update({ status:"unconfirmed" })}>未確認に戻す</button>}
      </div>}
    </td>
    <td style={{ padding:"9px 7px", verticalAlign:"top", minWidth:180 }}>
      <textarea aria-label={`${check.display_service_user_name} ${check.item_label} メモ`} value={note} onChange={event=>setNote(event.target.value)} disabled={!canConfirm || saving} rows={2} style={{ width:"100%", resize:"vertical" }}/>
      {canConfirm && <button type="button" disabled={saving || note === (check.note || "")} onClick={()=>update({ note })} style={{ marginTop:3 }}>メモ保存</button>}
    </td>
  </tr>;
}

export const billingYearMonthInJapan = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:"Asia/Tokyo", year:"numeric", month:"2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part=>[part.type, part.value]));
  return `${values.year}-${values.month}`;
};

export default function BillingMonthGenerator({ store, request }) {
  const [ym, setYm] = useState(billingYearMonthInJapan());
  const [closingDueDate, setClosingDueDate] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [monthAction, setMonthAction] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [auditLogs, setAuditLogs] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const fetchMonthData = useCallback(async () => {
    const data = await request(`/billing_monthly_checks.php?storeId=${encodeURIComponent(store.id)}&ym=${encodeURIComponent(ym)}`);
    let logs = [];
    if (data.billing_month && data.capabilities?.can_view_audit !== false) {
      const auditData = await request(`/billing_month_audits.php?storeId=${encodeURIComponent(store.id)}&ym=${encodeURIComponent(ym)}`);
      logs = auditData.audit_logs || [];
    }
    return { data, logs };
  }, [request, store.id, ym]);

  const applyMonthData = useCallback(({ data, logs }) => {
    setSummary(data);
    setAuditLogs(logs);
    setClosingDueDate(data.billing_month?.closing_due_date || "");
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setMessage("");
    fetchMonthData()
      .then(result=>{
        if (!active) return;
        applyMonthData(result);
        setError("");
      })
      .catch(requestError=>active && setError(requestError.message))
      .finally(()=>active && setLoading(false));
    return () => { active = false; };
  }, [applyMonthData, fetchMonthData]);

  const refreshMonth = async () => {
    const result = await fetchMonthData();
    applyMonthData(result);
  };

  const generate = async () => {
    if (!summary?.billing_month && !closingDueDate) {
      setError("初回生成には確認期限を入力してください");
      return;
    }
    setGenerating(true);
    setError("");
    setMessage("");
    try {
      const data = await request("/billing_monthly_checks.php", "POST", {
        storeId:store.id,
        ym,
        closingDueDate:closingDueDate || undefined,
      });
      setSummary(data);
      setClosingDueDate(data.billing_month?.closing_due_date || closingDueDate);
      setMessage(`${data.generated.added}件を追加しました（合計${data.generated.total}件）`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setGenerating(false);
    }
  };

  const updateCheck = async (id, changes) => {
    setError("");
    setMessage("");
    try {
      const data = await request("/billing_monthly_checks.php", "PUT", { id, ...changes });
      setSummary(data);
      setMessage("月次確認行を更新しました");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const closeMonth = async () => {
    if (!window.confirm(`${ym}を月締めしますか？締め後は請求内容を編集できません。`)) return;
    setMonthAction("close");
    setError("");
    setMessage("");
    try {
      await request("/billing_month_close.php", "POST", { storeId:store.id, ym });
      await refreshMonth();
      setMessage("月締めしました");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setMonthAction("");
    }
  };

  const reopenMonth = async () => {
    const trimmedReason = reopenReason.trim();
    const reasonLength = [...reopenReason].length;
    if (!trimmedReason) {
      setError("再オープン理由を入力してください");
      return;
    }
    if (reasonLength > 500) {
      setError("再オープン理由は500文字以内で入力してください");
      return;
    }
    if (!window.confirm("再オープン後は請求内容を再編集できます。再オープンしますか？")) return;
    setMonthAction("reopen");
    setError("");
    setMessage("");
    try {
      await request("/billing_month_reopen.php", "POST", { storeId:store.id, ym, reason:reopenReason });
      await refreshMonth();
      setReopenReason("");
      setMessage("請求月を再オープンしました");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setMonthAction("");
    }
  };

  const canGenerate = Boolean(summary?.capabilities?.can_generate);
  const canConfirm = Boolean(summary?.capabilities?.can_confirm);
  const billingProhibited = Boolean(summary?.billing_policy?.billing_prohibited);
  const isClosed = summary?.billing_month?.status === "closed";
  const canClose = Boolean(summary?.capabilities?.can_close) && !isClosed && !billingProhibited;
  const canReopen = Boolean(summary?.capabilities?.can_reopen) && isClosed;
  const reopenReasonLength = [...reopenReason].length;
  const checks = summary?.checks || [];
  const calendarSummary = summary?.calendar_summary || {};
  const latestClose = [...auditLogs].reverse().find(log=>log.action === "close");
  return <section aria-label="月次確認行生成" style={{ background:"#fff", border:"1px solid #dbeafe", borderRadius:11, padding:"13px 15px", marginBottom:15 }}>
    <div style={{ display:"flex", justifyContent:"space-between", gap:10, flexWrap:"wrap", alignItems:"flex-end" }}>
      <div>
        <div style={{ fontWeight:800, fontSize:14, color:store.color }}>月次確認の準備</div>
        <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>対象月に確認すべき利用者別請求項目を生成します。再実行しても既存行は変更しません。</div>
      </div>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"flex-end" }}>
        <label style={{ fontSize:11, color:"#555" }}>対象年月
          <input aria-label="月次確認対象年月" type="month" value={ym} onChange={event=>setYm(event.target.value)} style={{ display:"block", padding:"6px 8px" }}/>
        </label>
        <label style={{ fontSize:11, color:"#555" }}>確認期限{summary?.billing_month ? "" : "（初回必須）"}
          <input aria-label="月次確認期限" type="date" value={closingDueDate} onChange={event=>setClosingDueDate(event.target.value)} disabled={Boolean(summary?.billing_month)} style={{ display:"block", padding:"6px 8px" }}/>
        </label>
        {canGenerate && <button type="button" onClick={generate} disabled={generating || loading} style={{ background:store.accent, color:"#fff", border:0, borderRadius:7, padding:"8px 13px", fontWeight:700 }}>{generating ? "生成中…" : "確認項目を生成"}</button>}
      </div>
    </div>
    {billingProhibited && <div role="status" style={{ marginTop:10, padding:"10px 12px", border:"1px solid #f59e0b", borderRadius:8, background:"#fffbeb", color:"#92400e", fontWeight:800 }}>2026年9月は練習月・請求禁止です。入力内容の確認はできますが、月締めはできません。</div>}
    {loading ? <div style={{ fontSize:12, color:"#64748b", marginTop:8 }}>月次情報を確認中…</div> : <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:9, fontSize:12 }}>
      <span>確認行 <b>{summary?.counts?.total || 0}件</b></span>
      <span>未確認 <b>{calendarSummary.unconfirmed_count ?? summary?.counts?.unconfirmed ?? 0}件</b></span>
      <span>実費未入力・要確認 <b>{calendarSummary.missing_action_required_count || 0}件</b></span>
      <span>期限超過・未確認 <b>{calendarSummary.overdue_unconfirmed_count || 0}件</b></span>
      {summary?.billing_month && <span>状態 <b>{isClosed ? "締め済み" : "編集中"}</b></span>}
      {isClosed && summary.billing_month.closed_at && <span>締め日時 <b>{formatAuditDateTime(summary.billing_month.closed_at)}</b></span>}
      {isClosed && (latestClose?.actor?.name || summary.billing_month.closed_by) && <span>締め実行者 <b>{latestClose?.actor?.name || summary.billing_month.closed_by}</b></span>}
    </div>}
    {!loading && canClose && <div style={{ marginTop:10 }}>
      <button type="button" onClick={closeMonth} disabled={Boolean(monthAction)} style={{ background:"#334155", color:"#fff", border:0, borderRadius:7, padding:"8px 13px", fontWeight:700 }}>
        {monthAction === "close" ? "月締め中…" : "月締めする"}
      </button>
    </div>}
    {!loading && canReopen && <div style={{ marginTop:10, padding:"10px 12px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8 }}>
      <label style={{ display:"block", fontSize:12, fontWeight:700 }}>再オープン理由（必須）
        <textarea aria-label="再オープン理由" value={reopenReason} onChange={event=>setReopenReason(event.target.value)} rows={2} style={{ display:"block", width:"100%", marginTop:5, resize:"vertical" }}/>
      </label>
      <div style={{ fontSize:11, color:reopenReasonLength > 500 ? "#b91c1c" : "#64748b", marginTop:3 }}>{reopenReasonLength}/500文字</div>
      <button type="button" onClick={reopenMonth} disabled={Boolean(monthAction) || !reopenReason.trim() || reopenReasonLength > 500} style={{ marginTop:6 }}>
        {monthAction === "reopen" ? "再オープン中…" : "再オープンする"}
      </button>
    </div>}
    {message && <div role="status" style={{ color:"#166534", fontSize:12, marginTop:8 }}>{message}</div>}
    {error && <div role="alert" style={{ color:"#b91c1c", fontSize:12, marginTop:8 }}>{error}</div>}
    {!loading && summary?.billing_month && <div style={{ overflowX:"auto", marginTop:12 }}>
      {checks.length === 0 ? <div style={{ fontSize:12, color:"#64748b" }}>確認行はありません。必要に応じて確認項目を生成してください。</div> : <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
        <thead><tr style={{ textAlign:"left", borderBottom:"1px solid #cbd5e1", color:"#475569" }}>
          <th style={{ padding:"6px 7px" }}>利用者</th><th style={{ padding:"6px 7px" }}>請求項目</th><th style={{ padding:"6px 7px" }}>実費照合</th><th style={{ padding:"6px 7px" }}>確認状態</th><th style={{ padding:"6px 7px" }}>メモ</th>
        </tr></thead>
        <tbody>{checks.map(check=><BillingCheckRow key={check.id} check={check} canConfirm={canConfirm} onUpdate={updateCheck}/>)}</tbody>
      </table>}
    </div>}
    {!loading && summary?.billing_month && <section aria-label="締め履歴" style={{ marginTop:14, paddingTop:11, borderTop:"1px solid #e2e8f0" }}>
      <div style={{ fontWeight:800, fontSize:13 }}>締め履歴</div>
      {auditLogs.length === 0 ? <div style={{ fontSize:12, color:"#64748b", marginTop:5 }}>締め履歴はありません。</div> : <ol style={{ margin:"7px 0 0", paddingLeft:21 }}>
        {auditLogs.map(log=><li key={log.id} style={{ marginBottom:8, fontSize:12 }}>
          <div><b>{formatAuditDateTime(log.created_at)}　{auditActionLabel(log.action)}</b></div>
          <div>実行者：{log.actor?.name || log.actor?.id || "不明"}</div>
          {log.action === "reopen" && log.reason && <div>理由：{log.reason}</div>}
        </li>)}
      </ol>}
    </section>}
  </section>;
}
